<template>
  <div :class="isMobile ? 'flex flex-col gap-3 mt-3' : 'flex flex-row justify-between items-center gap-3'">
    <!-- User MultiSelect - Only for admin -->
    <div v-if="showUserSelect" class="w-full sm:w-auto">
      <MultiSelect 
        v-model="selectedUsersComputed"
        :loading="loading"
        placeholder="Select Publishers" 
        :options="users"
        :maxSelectedLabels="2" 
        optionValue="userId" 
        optionLabel="userName"
        selectedItemsLabel="{0} publishers selected" 
        @change="handleUserSelectedChange"
        class="w-full sm:w-auto"
      />
    </div>

    <!-- Date Picker Group -->
    <div class="flex items-center gap-2 w-full sm:w-auto">
      <DatePicker v-model="datesComputed" selectionMode="range" showIcon :showOnFocus="true" class="w-full sm:w-auto" />
    </div>

    <!-- Month Selection - Only show on desktop -->
    <div v-if="!isMobile" class="flex flex-wrap gap-1 justify-center">
      <Button v-for="(month, index) in months" :key="index" :label="month.label"
        @click="selectedMonth(index, month.start, month.end)"
        :severity="selectedMonthIndexComputed === index ? 'primary' : 'secondary'" 
        class="h-7" 
      />
    </div>

    <!-- Controls -->
    <div class="w-full sm:w-auto">
      <Select v-model="selectedDatePresetComputed" placeholder="Select date preset" :options="datePresets"
        optionLabel="name" @change="handleDatePresetChange" class="w-full sm:w-auto" 
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  dates: {
    type: Array,
    required: true
  },
  months: {
    type: Array,
    required: true
  },
  selectedMonthIndex: {
    type: Number,
    required: true
  },
  selectedDatePreset: {
    type: Object,
    default: null
  },
  datePresets: {
    type: Array,
    required: true
  },
  isMobile: {
    type: Boolean,
    required: true
  },
  // New props for admin user select
  showUserSelect: {
    type: Boolean,
    default: false
  },
  selectedUsers: {
    type: Array,
    default: () => []
  },
  users: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  'update:dates', 
  'update:selectedMonthIndex', 
  'update:selectedDatePreset',
  'update:selectedUsers',
  'monthSelect', 
  'datePresetChange',
  'userSelectedChange'
]);

// Existing computed properties
const datesComputed = computed({
  get: () => props.dates,
  set: (value) => emit('update:dates', value)
});

const selectedMonthIndexComputed = computed({
  get: () => props.selectedMonthIndex,
  set: (value) => emit('update:selectedMonthIndex', value)
});

const selectedDatePresetComputed = computed({
  get: () => props.selectedDatePreset,
  set: (value) => emit('update:selectedDatePreset', value)
});

// New computed property for users
const selectedUsersComputed = computed({
  get: () => props.selectedUsers,
  set: (value) => emit('update:selectedUsers', value)
});

const selectedMonth = (index, start, end) => {
  emit('monthSelect', { index, start, end });
};

const handleDatePresetChange = () => {
  emit('datePresetChange');
};

const handleUserSelectedChange = () => {
  emit('userSelectedChange');
};
</script>

<style scoped>
:deep(.p-calendar) {
  width: 100%;
}

:deep(.p-calendar .p-inputtext) {
  width: 100%;
}

:deep(.p-dropdown) {
  width: 100%;
}

:deep(.p-multiselect) {
  width: 100%;
}

@media (min-width: 640px) {
  :deep(.p-calendar),
  :deep(.p-calendar .p-inputtext),
  :deep(.p-dropdown),
  :deep(.p-multiselect) {
    width: auto;
  }
  
  :deep(.p-dropdown),
  :deep(.p-multiselect) {
    min-width: 200px;
  }
}
</style> 